import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Check, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Generate a stable browser fingerprint
function getFingerprint() {
  const stored = localStorage.getItem('med_poll_fp');
  if (stored) return stored;
  const parts = [
    navigator.userAgent,
    screen.width + 'x' + screen.height,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.language,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || '',
    navigator.maxTouchPoints || '',
  ];
  let hash = 0;
  const str = parts.join('|');
  for (let i = 0; i < str.length; i++) {
    const chr = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + chr;
    hash |= 0;
  }
  const fp = 'fp_' + Math.abs(hash).toString(36) + '_' + Date.now().toString(36);
  localStorage.setItem('med_poll_fp', fp);
  return fp;
}

const PollSection = () => {
  const [polls, setPolls] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [votedPolls, setVotedPolls] = useState({});
  const [voting, setVoting] = useState(false);
  const [showResults, setShowResults] = useState({});
  const wsRef = useRef(null);
  const fingerprint = useRef(getFingerprint());

  const fetchPolls = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/polls/active`);
      if (r.ok) {
        const data = await r.json();
        // Only show polls that have candidates
        const withCandidates = data.filter(p => p.candidates && p.candidates.length > 0);
        setPolls(withCandidates);
        // Check vote status for each poll
        for (const poll of withCandidates) {
          const vr = await fetch(`${API}/api/polls/${poll.id}/check-vote?fingerprint=${fingerprint.current}`);
          if (vr.ok) {
            const vd = await vr.json();
            if (vd.voted) {
              setVotedPolls(prev => ({ ...prev, [poll.id]: vd.candidate_id }));
              setShowResults(prev => ({ ...prev, [poll.id]: true }));
            }
          }
        }
      }
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { fetchPolls(); }, [fetchPolls]);

  // WebSocket for real-time updates
  useEffect(() => {
    const wsUrl = API.replace(/^http/, 'ws') + '/api/ws/polls';
    let ws;
    let retryTimer;

    function connect() {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        if (event.data === 'pong') return;
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'poll_update') {
            setPolls(prev => prev.map(p =>
              p.id === msg.poll_id
                ? { ...p, results: msg.results, total_votes: msg.total_votes }
                : p
            ));
          } else if (msg.type === 'poll_status') {
            fetchPolls();
          }
        } catch (e) { /* silent */ }
      };

      ws.onclose = () => { retryTimer = setTimeout(connect, 3000); };
      ws.onerror = () => ws.close();
    }

    connect();
    return () => {
      clearTimeout(retryTimer);
      if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); }
    };
  }, [fetchPolls]);

  const handleVote = async (pollId, candidateId) => {
    if (voting) return;
    if (votedPolls[pollId]) {
      toast.info('You already voted in this category');
      setShowResults(prev => ({ ...prev, [pollId]: true }));
      return;
    }
    setVoting(true);
    try {
      const r = await fetch(`${API}/api/polls/${pollId}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_id: candidateId, fingerprint: fingerprint.current }),
      });
      if (r.ok) {
        const data = await r.json();
        setVotedPolls(prev => ({ ...prev, [pollId]: candidateId }));
        setShowResults(prev => ({ ...prev, [pollId]: true }));
        setPolls(prev => prev.map(p =>
          p.id === pollId ? { ...p, results: data.results, total_votes: data.total_votes } : p
        ));
        toast.success('Vote recorded!');
      } else if (r.status === 409) {
        setVotedPolls(prev => ({ ...prev, [pollId]: candidateId }));
        setShowResults(prev => ({ ...prev, [pollId]: true }));
        toast.info('You already voted in this category');
      }
    } catch (e) { /* silent */ }
    setVoting(false);
  };

  if (polls.length === 0) return null;

  const activePoll = polls[activeTab] || polls[0];
  const hasVoted = !!votedPolls[activePoll?.id];
  const showingResults = showResults[activePoll?.id];

  return (
    <section className="px-4 pb-16" data-testid="poll-section">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="glass-card overflow-hidden"
        >
          {/* Header */}
          <div className="px-5 pt-5 pb-3">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-[#EF4444] animate-pulse" />
              <span className="text-[#EF4444] text-[10px] font-bold tracking-widest uppercase">Live Poll</span>
            </div>

            {/* Category Tabs */}
            {polls.length > 1 && (
              <div className="flex gap-1.5 mb-4" data-testid="poll-tabs">
                {polls.map((poll, idx) => {
                  const shortTitle = poll.title.replace(/\s+on\s+clubhouse/i, '').trim();
                  const voted = !!votedPolls[poll.id];
                  return (
                    <button
                      key={poll.id}
                      onClick={() => setActiveTab(idx)}
                      data-testid={`poll-tab-${idx}`}
                      className={`flex-1 px-3 py-2.5 rounded-lg text-[10px] font-bold tracking-wider uppercase transition-all relative ${
                        activeTab === idx
                          ? 'bg-[#E91E8D]/20 text-[#E91E8D] border border-[#E91E8D]/30'
                          : 'bg-white/5 text-white/30 border border-white/5 active:bg-white/10'
                      }`}
                    >
                      {shortTitle}
                      {voted && <Check size={8} className="inline ml-1 text-green-400" />}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Poll Title */}
            <h3 className="text-white font-bold text-sm sm:text-base leading-tight" data-testid="poll-title">
              {activePoll.title}
            </h3>
            {activePoll.description && (
              <p className="text-white/30 text-xs mt-1">{activePoll.description}</p>
            )}
            {activePoll.status === 'paused' && (
              <div className="mt-2 px-2 py-1 bg-yellow-500/10 border border-yellow-500/20 rounded text-yellow-400 text-[10px] font-bold inline-block">
                VOTING PAUSED
              </div>
            )}
          </div>

          {/* Candidates / Results */}
          <div className="px-5 pb-5">
            {(showingResults || hasVoted) ? (
              /* Ranked Results */
              <div className="space-y-1.5" data-testid="poll-results">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white/20 text-[10px] font-bold tracking-wider uppercase">
                    {activePoll.total_votes || 0} total votes
                  </span>
                  {hasVoted && (
                    <span className="text-green-400 text-[10px] font-bold flex items-center gap-1">
                      <Check size={10} /> Voted
                    </span>
                  )}
                </div>
                {(activePoll.results || []).slice(0, 10).map((r, idx) => (
                  <div key={r.candidate_id} className="relative" data-testid={`poll-result-${idx}`}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/5 relative overflow-hidden">
                      {/* Progress bar */}
                      <div
                        className="absolute inset-0 rounded-lg transition-all duration-500"
                        style={{
                          background: idx === 0
                            ? 'linear-gradient(90deg, rgba(233,30,141,0.15) 0%, transparent 100%)'
                            : 'linear-gradient(90deg, rgba(255,255,255,0.03) 0%, transparent 100%)',
                          width: `${Math.max(r.percentage, 2)}%`,
                        }}
                      />
                      <span className={`text-xs font-bold w-5 text-center relative z-10 ${
                        idx === 0 ? 'text-[#E91E8D]' : idx < 3 ? 'text-white/60' : 'text-white/25'
                      }`}>
                        {idx === 0 && <ChevronUp size={10} className="inline -mt-0.5" />}
                        {r.rank}
                      </span>
                      <span className={`text-xs font-medium flex-1 relative z-10 ${
                        idx === 0 ? 'text-white' : 'text-white/60'
                      }`}>
                        {r.name}
                      </span>
                      <span className={`text-xs font-bold relative z-10 ${
                        idx === 0 ? 'text-[#E91E8D]' : 'text-white/30'
                      }`}>
                        {r.percentage}%
                      </span>
                      {hasVoted && votedPolls[activePoll.id] === r.candidate_id && (
                        <Check size={10} className="text-green-400 relative z-10" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Voting View */
              <div className="space-y-1.5" data-testid="poll-voting">
                <p className="text-white/20 text-[10px] font-bold tracking-wider uppercase mb-2">
                  Tap to vote
                </p>
                {(activePoll.candidates || []).map((c, idx) => (
                  <button
                    key={c.id}
                    onClick={() => handleVote(activePoll.id, c.id)}
                    disabled={voting || activePoll.status === 'paused'}
                    data-testid={`poll-candidate-${idx}`}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-lg bg-white/[0.03] border border-white/5 hover:border-[#E91E8D]/30 active:bg-[#E91E8D]/10 transition-all text-left disabled:opacity-40"
                  >
                    <span className="text-white/20 text-xs font-bold w-5 text-center">{idx + 1}</span>
                    <span className="text-white/70 text-xs font-medium flex-1">{c.name}</span>
                    <span className="text-[#E91E8D] text-[9px] font-bold tracking-wider uppercase px-2 py-1 rounded bg-[#E91E8D]/10">Vote</span>
                  </button>
                ))}
                {activePoll.total_votes > 0 && (
                  <button
                    onClick={() => setShowResults(prev => ({ ...prev, [activePoll.id]: true }))}
                    className="w-full text-center text-white/20 text-[10px] font-bold mt-2 active:text-white/40 transition-colors"
                  >
                    View Results
                  </button>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PollSection;
